import { faChevronDown, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import IconButton from 'components/ui/Button/IconButton';
import Flex from 'components/ui/Flex';
import Link from 'components/ui/Link';
import Text from 'components/ui/Text';
import useAppSelector from 'hooks/useAppSelector';
import { CatalogNodeModel } from 'microshop-api';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CategoryUrlMap, selectCategoriesRaw } from 'store/reducers/assortmentSlice';
import styled, { ThemeColors } from 'styled-components';

type CategoriesProps = {
    selectedCategory: CategoryUrlMap;
};

const Categories: React.FC<CategoriesProps> = ({ selectedCategory }) => {
    const categoriesRaw = useAppSelector(selectCategoriesRaw);
    const location = useLocation();

    const isSearchPage = location.pathname === '/search';

    // Since we dont want to render categories on search page
    if (isSearchPage || !categoriesRaw?.length) return null;

    return (
        <CategoryTreeWrapper className="mb-3">
            {categoriesRaw.map((category, index) => (
                <CategoryTree
                    key={category.code}
                    category={category}
                    selectedCategory={selectedCategory}
                    startExpanded={index === 0 && categoriesRaw?.length === 1}
                />
            ))}
        </CategoryTreeWrapper>
    );
};

export default Categories;

type CategoryTreeProps = {
    category: CatalogNodeModel;
    parentSlug?: string;
    selectedCategory: { code?: string };
    startExpanded: boolean;
};

const CategoryTree = ({ category, parentSlug = '', selectedCategory, startExpanded }: CategoryTreeProps) => {
    const [isExpanded, setIsExpanded] = useState(startExpanded);

    const currentSlug = `${parentSlug}/${category.assortmentSlug ?? category.name}`;
    const hasChildren = Boolean(category.children?.length);

    return (
        <div>
            <div key={category?.code}>
                <StyledFlex align="center" justify="between">
                    <FilterElement noBorder>
                        <StyledLink noStyle={selectedCategory?.code !== category?.code} to={currentSlug}>
                            <Text
                                large
                                light={selectedCategory?.code !== category?.code}
                                bold={selectedCategory?.code === category?.code}
                            >
                                {category.name}
                            </Text>
                        </StyledLink>
                    </FilterElement>

                    {hasChildren && (
                        <IconButton
                            onClick={() => setIsExpanded((prev) => !prev)}
                            icon={isExpanded ? faChevronDown : faChevronRight}
                            small
                            bgColor="fill"
                        />
                    )}
                </StyledFlex>
                {hasChildren && (
                    <StyledCategoryChildren isExpanded={isExpanded}>
                        {category.children!.map((childCategory) => (
                            <CategoryTree
                                key={childCategory.code}
                                category={childCategory}
                                parentSlug={currentSlug}
                                selectedCategory={selectedCategory}
                                startExpanded={false}
                            />
                        ))}
                    </StyledCategoryChildren>
                )}
            </div>
        </div>
    );
};

const CategoryTreeWrapper = styled.div``;

const StyledCategoryChildren = styled.div<{ isExpanded: boolean }>`
    display: ${(props) => (props.isExpanded ? 'block' : 'none')};
    margin-left: 1rem;
`;

const StyledLink = styled(Link)<{ color?: keyof ThemeColors }>`
    color: ${({ theme, color }) => theme.colors[color ?? 'text']} !important;
`;

const FilterRow = styled.div`
    padding: 5px 0;
`;

const FilterElement = styled(FilterRow)<{ noBorder?: boolean }>`
    ${({ noBorder, theme }) => !noBorder && `border-bottom: 1px solid ${theme.colors.border};`}

    a:hover {
        text-decoration: underline;
    }
`;

const StyledFlex = styled(Flex)`
    ${({ theme }) => `border-bottom: 1px solid ${theme.colors.border};`}
`;
